import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../../i18n/i18n';
import i18n from '../../../i18n/i18n';
import { AxiosError } from 'axios';
import {
  IGetAdminTeachersResponse,
  IGetAdminTeachersParams,
  IGetAdminTeacherByIdResponse,
  IUpdateAdminTeacherResponse,
  IUpdateAdminTeacherPayload,
  ITeacherDeleteResponse,
} from '../../../interface/classveew-admin/admin-management.interface';
import { IErrorResponse, IApiErrorResponse } from '../../../interface/main.interface';
import {
  getAdminTeachers,
  getAdminTeacherById,
  updateAdminTeacher,
  deleteAdminTeacher as deleteAdminTeacherService,
} from '../../../services/classveew-admin';

/**
 * Fetch all admin teachers with optional query parameters
 */
export const fetchAdminTeachers = createAsyncThunk<
  IGetAdminTeachersResponse,
  IGetAdminTeachersParams,
  { rejectValue: IErrorResponse }
>('admin-teacher/fetch-all', async (params, { rejectWithValue }) => {
  try {
    const response = await getAdminTeachers(params);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || i18n.t('toast.failed_to_fetch_teachers', 'Failed to fetch teachers.'),
    });
  }
});

/**
 * Fetch a single admin teacher by ID
 */
export const fetchAdminTeacherById = createAsyncThunk<
  IGetAdminTeacherByIdResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-teacher/fetch-by-id', async (teacherId, { rejectWithValue }) => {
  try {
    const response = await getAdminTeacherById(teacherId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || tr('g.failed_to_fetch_teacher', 'Failed to fetch teacher.'),
    });
  }
});

/**
 * Update an admin teacher profile
 */
export const updateAdminTeacherProfile = createAsyncThunk<
  IUpdateAdminTeacherResponse,
  { teacherId: string; data: IUpdateAdminTeacherPayload },
  { rejectValue: IErrorResponse }
>('admin-teacher/update', async ({ teacherId, data }, { rejectWithValue }) => {
  try {
    const response = await updateAdminTeacher(teacherId, data);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IApiErrorResponse>;
    const errorData = error.response?.data || (error as IApiErrorResponse);
    return rejectWithValue({
      status: error.response?.status || errorData?.statusCode || 500,
      message: Array.isArray(errorData?.message)
        ? errorData.message.join(', ')
        : errorData?.message || i18n.t('toast.failed_to_update_teacher', 'Failed to update teacher.'),
    });
  }
});

/**
 * Permanently delete a teacher from the entire system.
 *
 * `adminInstance` rejects with the backend RESPONSE BODY (not an AxiosError), so
 * the caught value is inspected directly for its `message`.
 */
export const deleteAdminTeacher = createAsyncThunk<
  ITeacherDeleteResponse,
  string,
  { rejectValue: IErrorResponse }
>('admin-teacher/delete', async (teacherId, { rejectWithValue }) => {
  try {
    const response = await deleteAdminTeacherService(teacherId);
    return response.data;
  } catch (err) {
    const body = err as { message?: string };
    return rejectWithValue({
      message: body?.message || 'Failed to delete teacher',
    });
  }
});
